"use client";

import { useMemo, useState } from "react";

const questions = [
  {
    id: "goal",
    label: "What should the platform improve first?",
    help: "Choose the main buying outcome.",
    options: [
      ["", "Choose an outcome"],
      ["investigation", "Faster incident investigation"],
      ["consolidation", "Consolidate multiple tools"],
      ["cost", "Predictable observability costs"],
      ["otel", "Standardize on OpenTelemetry"],
      ["coverage", "Close monitoring gaps"],
      ["governance", "Meet governance requirements"],
    ],
  },
  {
    id: "ecosystem",
    label: "What must it support on day one?",
    help: "Describe your current telemetry stack.",
    options: [
      ["", "Choose your environment"],
      ["otel", "OpenTelemetry and Prometheus"],
      ["cloud", "Cloud-native telemetry"],
      ["agents", "Proprietary agents and integrations"],
      ["mixed", "Mixed legacy and cloud-native"],
      ["fresh", "Mostly greenfield"],
    ],
  },
  {
    id: "signals",
    label: "Which signals must correlate?",
    help: "Choose the minimum useful coverage.",
    options: [
      ["", "Choose required coverage"],
      ["infra", "Infrastructure metrics"],
      ["logsmetrics", "Metrics and logs"],
      ["three", "Metrics, logs, and traces"],
      ["full", "Full stack: RUM, synthetics, and profiles"],
    ],
  },
  {
    id: "deployment",
    label: "Where can the platform run?",
    help: "Use this as a hard constraint if needed.",
    options: [
      ["", "Choose a deployment model"],
      ["any", "No strong preference"],
      ["saas", "Vendor-managed SaaS"],
      ["byoc", "Private cloud / BYOC"],
      ["self", "Self-hosted or on-premises"],
      ["hybrid", "Hybrid deployment"],
    ],
  },
  {
    id: "governance",
    label: "What is your strictest governance requirement?",
    help: "Choose what could rule a platform out.",
    options: [
      ["", "Choose a governance level"],
      ["rbac", "Standard role-based access"],
      ["audit", "SSO, granular roles, and audit logs"],
      ["isolation", "Dataset or tenant isolation"],
      ["region", "Specific-region or in-country storage"],
      ["onprem", "No external data processing"],
    ],
  },
  {
    id: "operations",
    label: "How much can your team operate?",
    help: "Include storage, upgrades, scaling, and availability.",
    options: [
      ["", "Choose team capacity"],
      ["none", "None — fully managed"],
      ["shared", "Shared platform-team ownership"],
      ["dedicated", "Dedicated observability team"],
      ["full", "Comfortable operating the full stack"],
    ],
  },
  {
    id: "growth",
    label: "How will your telemetry grow?",
    help: "Choose the closest expected pattern.",
    options: [
      ["", "Choose a growth pattern"],
      ["stable", "Stable and predictable"],
      ["growing", "Rapidly growing"],
      ["cardinality", "High-cardinality dimensions"],
      ["bursty", "Bursty or strongly seasonal"],
      ["both", "High volume and high cardinality"],
      ["unknown", "Not yet measured"],
    ],
  },
  {
    id: "retention",
    label: "How long must detailed data stay searchable?",
    help: "Archive-only may not meet incident needs.",
    options: [
      ["", "Choose a retention requirement"],
      ["short", "15 days or less"],
      ["medium", "30–90 days"],
      ["year", "Up to one year"],
      ["long", "More than one year"],
      ["archive", "Long-term archive is acceptable"],
      ["custom", "Custom or regulatory retention rules"],
    ],
  },
  {
    id: "neutrality",
    label: "How portable must telemetry remain?",
    help: "Choose your tolerance for vendor lock-in.",
    options: [
      ["", "Choose a portability level"],
      ["1", "1 — Vendor-specific is acceptable"],
      ["2", "2 — Portability is useful but secondary"],
      ["3", "3 — Prefer open standards where practical"],
      ["4", "4 — Avoid proprietary core telemetry"],
      ["5", "5 — Backend neutrality is mandatory"],
    ],
  },
  {
    id: "pricing",
    label: "How predictable must pricing be?",
    help: "Consider growth, overages, retention, and seats.",
    options: [
      ["", "Choose a cost-control level"],
      ["1", "1 — Variable consumption is acceptable"],
      ["2", "2 — Broad forecasts are sufficient"],
      ["3", "3 — Clear usage reporting is required"],
      ["4", "4 — Budgets and controls are required"],
      ["5", "5 — Spend must be highly predictable"],
    ],
  },
] as const;

type QuestionId = (typeof questions)[number]["id"];
type Answers = Record<QuestionId, string>;

type PlatformProfile = {
  id: string;
  name: string;
  examples: string[];
  deployments: string[];
  summary: string;
};

const platformProfiles: PlatformProfile[] = [
  {
    id: "otel-managed",
    name: "OpenTelemetry-first managed platforms",
    examples: ["Dash0", "Honeycomb"],
    deployments: ["saas"],
    summary: "Start here when standard telemetry, low operating burden, and application investigation matter most.",
  },
  {
    id: "open-flexible",
    name: "Open ecosystem and self-managed platforms",
    examples: ["Grafana", "Elastic"],
    deployments: ["saas", "self", "hybrid"],
    summary: "Investigate these when open tooling, deployment control, and a broad integration ecosystem are priorities.",
  },
  {
    id: "integrated-saas",
    name: "Integrated managed observability suites",
    examples: ["Datadog", "New Relic"],
    deployments: ["saas"],
    summary: "Consider these when broad built-in coverage and a vendor-operated experience outweigh strict backend neutrality.",
  },
  {
    id: "enterprise-hybrid",
    name: "Enterprise and hybrid observability suites",
    examples: ["Dynatrace"],
    deployments: ["saas", "self", "hybrid"],
    summary: "Investigate this category for enterprise governance, automation, and mixed deployment requirements.",
  },
  {
    id: "cloud-scale",
    name: "Cloud-scale managed telemetry platforms",
    examples: ["Chronosphere"],
    deployments: ["saas"],
    summary: "Consider this category when rapid growth, high cardinality, and telemetry control are central concerns.",
  },
  {
    id: "byoc-verified",
    name: "Verified customer-cloud or BYOC offerings",
    examples: [],
    deployments: ["byoc"],
    summary: "Treat BYOC as its own architecture. Require vendors to document control-plane location, data-plane ownership, upgrades, networking, and failure responsibility before adding them to the shortlist.",
  },
];

const initialAnswers = Object.fromEntries(
  questions.map((question) => [question.id, ""]),
) as Answers;

function optionLabel(questionId: QuestionId, value: string) {
  const question = questions.find((item) => item.id === questionId);
  const option = question?.options.find((item) => item[0] === value);
  return option?.[1].replace(/^\d — /, "") ?? value;
}

function profileIsEligible(profile: PlatformProfile, answers: Answers) {
  if (answers.governance === "onprem") return profile.deployments.includes("self");
  if (answers.deployment === "any") return true;
  return profile.deployments.includes(answers.deployment);
}

export default function ObservabilityPlatformProfiler() {
  const [answers, setAnswers] = useState<Answers>(initialAnswers);
  const answered = Object.values(answers).filter(Boolean).length;

  const result = useMemo(() => {
    if (answered !== questions.length) return null;

    const neutrality = Number(answers.neutrality);
    const architecture = neutrality === 5
      ? "Backend-neutral required"
      : neutrality === 4
        ? "OpenTelemetry-first"
        : neutrality === 3
          ? "Standards-compatible"
          : "Vendor-integrated acceptable";
    const scopeNames: Record<string, string> = {
      infra: "Infrastructure-focused",
      logsmetrics: "Metrics-and-logs",
      three: "Three-signal",
      full: "Full-stack",
    };
    const ownershipNames: Record<string, string> = {
      none: "Vendor-managed",
      shared: "Shared ownership",
      dedicated: "Team-led operations",
      full: "Team-operated",
    };
    const scope = scopeNames[answers.signals];
    const ownership = ownershipNames[answers.operations];
    const deploymentNames: Record<string, string> = { any: "Flexible deployment", saas: "Managed SaaS", byoc: "Private cloud / BYOC", self: "Self-hosted", hybrid: "Hybrid" };
    const deployment = answers.governance === "onprem" ? "Self-hosted" : deploymentNames[answers.deployment];

    const conflicts: string[] = [];
    if (answers.deployment === "self" && answers.operations === "none") conflicts.push("You require self-hosting but selected no capacity to operate platform infrastructure. Consider managed private cloud or budget for a platform team.");
    if (answers.governance === "onprem" && !["any", "self", "hybrid"].includes(answers.deployment)) conflicts.push("Your governance answer prohibits external processing, which conflicts with the selected deployment model.");
    if (answers.deployment === "byoc" && answers.operations === "none") conflicts.push("BYOC still creates networking, security, upgrade, or account-administration responsibilities. Verify the responsibility split.");

    const hasHardConflict =
      (answers.deployment === "self" && answers.operations === "none") ||
      (answers.governance === "onprem" && !["any", "self", "hybrid"].includes(answers.deployment));

    const profileReasons = new Map<string, string[]>();
    const addProfile = (id: string, reason: string) => {
      const reasons = profileReasons.get(id) ?? [];
      if (!reasons.includes(reason)) reasons.push(reason);
      profileReasons.set(id, reasons);
    };

    if (answers.deployment === "byoc") {
      addProfile("byoc-verified", "You selected BYOC, which must be verified as a distinct operating model rather than inferred from self-hosting");
    } else if (neutrality >= 4) {
      addProfile("otel-managed", "Your portability requirement favors platforms designed around standard telemetry");
      addProfile("open-flexible", "Your portability requirement also favors open ecosystems and exportable configuration");
    } else if (neutrality <= 2) {
      addProfile("integrated-saas", "You are willing to trade some portability for an integrated managed experience");
      addProfile("enterprise-hybrid", "Vendor-integrated automation may fit your operating model");
    } else {
      addProfile("open-flexible", "You prefer standards without making backend neutrality a hard gate");
      addProfile("integrated-saas", "A managed suite remains viable under your portability preference");
    }

    if (["self", "hybrid"].includes(answers.deployment)) {
      addProfile("open-flexible", "Your deployment requirement needs a supported self-managed or hybrid path");
      addProfile("enterprise-hybrid", "Your deployment requirement calls for enterprise hybrid options");
    }
    if (answers.signals === "full" || ["coverage", "consolidation"].includes(answers.goal)) {
      addProfile("integrated-saas", "Your required signal coverage favors a broad integrated suite");
      addProfile("enterprise-hybrid", "Your required signal coverage may benefit from enterprise full-stack tooling");
    }
    if (["mixed", "agents"].includes(answers.ecosystem)) {
      addProfile("integrated-saas", "Your existing estate needs mature agent and integration coverage");
      addProfile("open-flexible", "A heterogeneous estate may also benefit from an open integration ecosystem");
    }
    if (["growing", "cardinality", "bursty", "both", "unknown"].includes(answers.growth)) {
      addProfile("cloud-scale", "Your growth pattern makes scale, backpressure, cardinality, and cost behavior important PoC targets");
      addProfile("open-flexible", "Your growth pattern benefits from flexible telemetry pipelines and control points");
    }
    if (answers.operations === "none") {
      if (neutrality >= 3) addProfile("otel-managed", "Your team needs the vendor to operate the platform");
      if (neutrality <= 3 || ["coverage", "consolidation"].includes(answers.goal)) addProfile("integrated-saas", "Your team needs a fully managed service");
    }

    const profiles = hasHardConflict
      ? []
      : Array.from(profileReasons.entries())
          .map(([id, reasons]) => ({
            profile: platformProfiles.find((item) => item.id === id),
            reasons,
          }))
          .filter((item): item is { profile: PlatformProfile; reasons: string[] } =>
            Boolean(item.profile && profileIsEligible(item.profile, answers)),
          );

    const requirements = [optionLabel("goal", answers.goal)];
    if (answers.governance !== "rbac") requirements.push(optionLabel("governance", answers.governance));
    if (answers.deployment !== "any") requirements.push(optionLabel("deployment", answers.deployment));
    requirements.push(optionLabel("signals", answers.signals));
    requirements.push(optionLabel("retention", answers.retention));
    if (neutrality >= 3) requirements.push(optionLabel("neutrality", answers.neutrality));
    if (Number(answers.pricing) >= 3) requirements.push(optionLabel("pricing", answers.pricing));

    const tests: string[] = [];
    const addTest = (test: string) => {
      if (!tests.includes(test)) tests.push(test);
    };
    const goalTests: Record<string, string> = {
      investigation: "Run a blind incident exercise and measure time to an evidence-supported explanation",
      consolidation: "Inventory integrations, agents, migration work, and configuration automation",
      cost: "Model total cost at normal, 2×, 3×, and incident-burst volumes",
      otel: "Export the same OpenTelemetry data to a second backend and compare semantic fidelity",
      coverage: "Test every required workload and signal instead of relying on an integration count",
      governance: "Validate access boundaries, audit evidence, location, and deletion behavior",
    };

    const retentionTests: Record<string, string> = {
      short: "Verify the short searchable window and deletion behavior",
      medium: "Query the oldest data in the searchable retention window",
      year: "Benchmark historical queries and one-year storage economics",
      long: "Model multi-year storage, query performance, and lifecycle cost",
      archive: "Run an archive restore drill and measure restore time and cost",
      custom: "Validate retention, deletion, and legal-hold controls against policy",
    };
    const governanceTests: Record<string, string> = {
      rbac: "Test permitted and forbidden actions for representative user roles",
      audit: "Validate SSO, granular roles, revocation, and audit evidence",
      isolation: "Attempt forbidden cross-dataset or cross-tenant access",
      region: "Verify contractual data location and inspect the actual network path",
      onprem: "Verify every control-plane and data-plane flow remains within the approved boundary",
    };
    const growthTests: Record<string, string> = {
      stable: "Validate sustained throughput and steady-state query performance",
      growing: "Test ingestion, query performance, and cost at 2× and 3× scale",
      cardinality: "Run a cardinality stress test and measure query and billing effects",
      bursty: "Test burst ingestion, backpressure, recovery, and burst pricing",
      both: "Benchmark worst-case volume and cardinality together",
      unknown: "Measure the current telemetry baseline before comparing vendors",
    };
    const pricingTests: Record<string, string> = {
      "1": "Validate a quote against measured usage at the current scale",
      "2": "Compare cost at the current workload and at 2× growth",
      "3": "Reconcile platform usage reporting with measured telemetry",
      "4": "Test budgets, alerts, and controls against measured usage",
      "5": "Model cost variance and verify contractual caps or controls",
    };

    const governanceTest = governanceTests[answers.governance];
    if (governanceTest) addTest(governanceTest);
    if (["self", "byoc", "hybrid"].includes(answers.deployment)) addTest("Review the deployment RACI, then test upgrades, recovery, and network failure");
    const retentionTest = retentionTests[answers.retention];
    if (retentionTest) addTest(retentionTest);
    const goalTest = goalTests[answers.goal];
    if (goalTest) addTest(goalTest);
    const pricingTest = pricingTests[answers.pricing];
    if (pricingTest) addTest(pricingTest);
    const growthTest = growthTests[answers.growth];
    if (growthTest) addTest(growthTest);
    if (neutrality === 5) addTest("Require a dual-backend export test before treating backend neutrality as proven");
    else if (neutrality >= 3) addTest("Verify the supported OpenTelemetry path and preserved resource context");
    if (["full", "three", "logsmetrics"].includes(answers.signals)) addTest("Follow one incident across every required signal and verify exact correlation");

    return {
      architecture,
      scope,
      ownership,
      deployment,
      conflicts,
      profiles,
      requirements: requirements.slice(0, 6),
      tests: tests.slice(0, 8),
    };
  }, [answered, answers]);

  return (
    <section className="col-span-full my-8 overflow-hidden rounded-lg border border-grey-850 bg-grey-950 text-sm leading-relaxed text-grey-400 [&_h2]:m-0 [&_h3]:m-0 [&_h4]:m-0 [&_p]:m-0" aria-labelledby="platform-profiler-title">
      <header className="flex flex-col gap-4 border-b border-grey-850 p-4 sm:flex-row sm:justify-between sm:p-6">
        <div className="min-w-0">
          <h2 id="platform-profiler-title">Find your observability platform fit</h2>
          <p>Answer ten questions for a platform profile, investigation categories, and focused PoC tests.</p>
        </div>
        <span className="self-start whitespace-nowrap rounded-sm bg-grey-900 px-2 py-1 font-mono text-xs tabular-nums">{answered}/{questions.length} answered</span>
      </header>

      <div className="overflow-x-auto custom-scrollbar scrollbar-horizontal">
        <table className="portable-table table! my-0! min-w-lg table-fixed [&_th]:font-medium [&_th]:border-grey-850 [&_td]:border-grey-850 [&_th]:p-4 [&_td]:p-4 sm:[&_th]:p-6 sm:[&_td]:p-6">
          <thead><tr><th scope="col">Requirement</th><th scope="col">Your answer</th></tr></thead>
          <tbody>
            {questions.map((question, index) => (
              <tr key={question.id}>
                <th scope="row" className="w-1/2 min-w-48 align-middle">
                  <span className="block text-white"><span className="text-grey-550 tabular-nums">{index + 1}.</span> {question.label}</span>
                  <span className="mt-1 block text-xs font-normal text-grey-400">{question.help}</span>
                </th>
                <td className="w-1/2 min-w-48 align-middle">
                  <label className="sr-only" htmlFor={`platform-profiler-${question.id}`}>{question.label}</label>
                  <select
                    id={`platform-profiler-${question.id}`}
                    value={answers[question.id]}
                    className={`min-h-12 w-full min-w-0 rounded border border-grey-700 border-l-4 bg-grey-900 p-2 text-sm text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red ${answers[question.id] ? "border-l-teal" : "border-l-yellow"}`}
                    onChange={(event) => setAnswers((current) => ({ ...current, [question.id]: event.target.value }))}
                  >
                    {question.options.map(([value, label]) => <option key={value || "empty"} value={value}>{label}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!result ? (
        <p className="p-4 sm:p-6">Answer all ten questions to generate your requirements profile and investigation starting points.</p>
      ) : (
        <div className="flex flex-col gap-6 p-4 sm:p-6" aria-live="polite">
          <span className="block font-mono text-xs uppercase tracking-widest text-red">Your requirements profile</span>
          <h3>{result.ownership}, {result.architecture}, {result.scope.toLowerCase()} platform</h3>
          <p>Your main objective: {optionLabel("goal", answers.goal)}. Apply deployment and governance constraints before comparing softer preferences.</p>

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {[["Deployment", result.deployment], ["Architecture", result.architecture], ["Coverage", result.scope], ["Ownership", result.ownership]].map(([label, value]) => (
              <div key={label} className="rounded-lg border border-grey-850 p-4"><span className="block text-xs text-grey-550">{label}</span><strong className="mt-1 block font-medium text-white">{value}</strong></div>
            ))}
          </div>

          {result.conflicts.length > 0 && <div className="rounded-r border-l-2 border-red bg-grey-900 p-4 text-white">{result.conflicts.join(" ")}</div>}

          <div>
            <span className="block font-mono text-xs uppercase tracking-widest text-red">Platform categories worth investigating</span>
            <div className="mt-4 grid gap-4 xl:grid-cols-2 [&_article]:min-w-0 [&_article]:rounded-lg [&_article]:bg-grey-900 [&_article]:p-4 [&_h4]:text-white [&_p]:mt-3 [&_strong]:font-medium [&_strong]:text-white">
              {result.profiles.length > 0 ? result.profiles.map(({ profile, reasons }) => (
                <article key={profile.id}>
                  <span className="mb-2 block font-mono text-xxs uppercase tracking-widest text-grey-550">Category to investigate</span>
                  <h4>{profile.name}</h4>
                  {profile.examples.length > 0 && <p><strong>Examples:</strong> {profile.examples.join(", ")}</p>}
                  <p>{profile.summary}</p>
                  <p><strong>Why it appeared:</strong> {reasons.slice(0, 2).join("; ")}.</p>
                </article>
              )) : (
                <article className="col-span-full"><h4>Resolve the hard requirements first</h4><p>The selected deployment, governance, and operating constraints do not form a workable shortlist yet.</p></article>
              )}
            </div>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 [&_h4]:text-white [&_ul]:mt-3 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-5 marker:text-red">
            <div><h4>Your requirements and gates</h4><ul>{result.requirements.map((item) => <li key={item}>{item}</li>)}</ul></div>
            <div><h4>Emphasize these PoC tests</h4><ul>{result.tests.map((item) => <li key={item}>{item}</li>)}</ul></div>
          </div>

          <p className="text-grey-550">Dash0 publishes this guide. The profiler does not numerically score or rank vendors. Example vendors are editorial research starting points; verify current capabilities, regions, pricing, evidence, and contractual terms through the PoC.</p>
        </div>
      )}
    </section>
  );
}
